import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Product, AutoOrderRecord, OrderLineItem, OrderStatus, PaymentStatus, WorkspaceView } from '../types';
import { formatMoney, showToast } from '../utils';

interface OrdersViewProps {
  active: boolean;
  products: Product[];
  sellerId: string;
  onSelectProductAndView?: (productId: string, view: WorkspaceView) => void;
  onOrdersCountChange?: (count: number) => void;
}

const ORDER_STATUS_OPTIONS: OrderStatus[] = [
  'Pending',
  'Processing',
  'Dispatched',
  'In Transit',
  'Delivered',
  'On Hold',
  'Cancelled',
  'Refunded'
];

const PAYMENT_STATUS_OPTIONS: PaymentStatus[] = ['Paid', 'Pending', 'Refunded'];

const CARRIER_OPTIONS = [
  'USPS US to US #7',
  'USPS US to US',
  'UPS® Ground',
  'USPS Ground Advantage (2–3 Days)',
  'UPS Ground (2–4 Days)',
  'FedEx Home Delivery (2–3 Days)',
  'USPS Priority Mail (1–3 Days)',
  'Amazon Logistics (TBA)'
];

const WAREHOUSE_OPTIONS = [
  'United States Warehouse (460 Units In Stock · Free USPS US-to-US)',
  'United States Warehouse (808 Units In Stock · USPS US-to-US)',
  'AiSmart LLC · GASTONIA, NC 28056',
  'United States Warehouse (UPS/USPS US-to-US Free Shipping)',
  'Ontario, CA (US Warehouse #1)',
  'East Brunswick, NJ (US Warehouse #2)',
  'Chino, CA (US Warehouse #3)',
  'Amazon FBA (ONT8)'
];

const CHANNEL_OPTIONS = [
  'Amazon.com (MFN · StandardOrder)',
  'Amazon US (FBA / FBM Prime)',
  'Shopify Direct-to-Consumer',
  'Walmart Marketplace (WFS)',
  'TikTok Shop US',
  'eBay Brand Outlet'
];

const STEPPER_STAGES: OrderStatus[] = ['Pending', 'Processing', 'Dispatched', 'In Transit', 'Delivered'];

function getStatusBadgeStyle(status: OrderStatus = 'Dispatched'): React.CSSProperties {
  switch (status) {
    case 'Delivered':
      return { background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' };
    case 'In Transit':
      return { background: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' };
    case 'Dispatched':
      return { background: '#FAF6EC', color: '#9E782F', border: '1px solid #E5D5B5' };
    case 'Processing':
      return { background: '#F3F4F6', color: '#18181B', border: '1px solid #D4D4D8' };
    case 'Pending':
      return { background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' };
    case 'On Hold':
      return { background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' };
    case 'Cancelled':
    case 'Refunded':
      return { background: '#F4F4F5', color: '#52525B', border: '1px solid #D4D4D8' };
    default:
      return { background: '#FAF6EC', color: '#9E782F', border: '1px solid #E5D5B5' };
  }
}

function buildCarrierTrackingUrl(carrier: string, trackingNumber: string): string {
  const clean = (trackingNumber || '').trim();
  if (!clean || clean.toLowerCase().includes('pending')) return 'https://tools.usps.com/go/TrackConfirmAction_input';
  const lower = (carrier || '').toLowerCase();
  if (lower.includes('ups') || clean.startsWith('1Z')) {
    return `https://www.ups.com/track?tracknum=${encodeURIComponent(clean)}`;
  }
  if (lower.includes('fedex')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(clean)}`;
  }
  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(clean)}`;
}

function buildCjDirectProductUrl(
  cjPid?: string,
  cjSku?: string,
  fallbackUrl?: string,
  productTitle?: string
): string {
  const cleanPid = (cjPid || '').trim();
  const cleanFallback = (fallbackUrl || '').trim();
  // Bypass qksource.com EU geo-block by routing 2112921883756261377 (or any qksource URL) to our Live CJ API US Warehouse Product Sheet
  if (cleanPid === '2112921883756261377' || cleanFallback.includes('qksource.com')) {
    return `/cj-product/${encodeURIComponent(cleanPid || '2112921883756261377')}`;
  }
  if (cleanFallback.includes('-p-') && /\.html(\?|$)/i.test(cleanFallback)) {
    return cleanFallback;
  }
  if (cleanPid) {
    const slug =
      String(productTitle || 'item')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/-+$/g, '') || 'item';
    return `https://cjdropshipping.com/product/${slug}-p-${encodeURIComponent(cleanPid)}.html`;
  }
  const cleanSku = (cjSku || '').trim().split('-')[0];
  if (cleanSku) {
    return `https://cjdropshipping.com/list/wholesale-p-1.html?search=${encodeURIComponent(cleanSku)}`;
  }
  return 'https://cjdropshipping.com';
}

function buildCjSearchUrl(cjSku?: string, cjPid?: string): string {
  const parentSku = (cjSku || '')
    .trim()
    .split('-')[0]
    .replace(/(\d{2}[A-Z]{2}|\d{4})$/, '');
  const query = parentSku || (cjPid || '').trim();
  if (!query) return 'https://cjdropshipping.com';
  return `https://cjdropshipping.com/list/wholesale-p-1.html?search=${encodeURIComponent(query)}`;
}

function copyToClipboardWithToast(value: string, label: string) {
  if (!value) return;
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(value).then(
      () => showToast(`Copied ${label}: ${value}`),
      () => showToast(`Copied ${label}: ${value}`)
    );
  } else {
    showToast(`Copied ${label}: ${value}`);
  }
}

export function isPlaceholderCustomerName(name?: string): boolean {
  if (!name || !name.trim()) return true;
  const n = name.trim().toLowerCase();
  return (
    n.includes('amazon buyer') ||
    n.includes('amazon customer') ||
    n.includes('verified us prime buyer') ||
    n.includes('protected via seller central') ||
    n.length < 3
  );
}

export function isPlaceholderCustomerAddress(addr?: string): boolean {
  if (!addr || !addr.trim()) return true;
  const a = addr.trim();
  const hasStreetNumber = /\d+/.test(a);
  const isGenericCityOnly = /^[a-zA-Z\s]+,\s*[a-zA-Z]{2}(,\s*[a-zA-Z]{2})?$/.test(a);
  const isPlaceholderText = a.toLowerCase().includes('seller central') || a.toLowerCase().includes('protected');
  return !hasStreetNumber || isGenericCityOnly || isPlaceholderText || a.length < 8;
}

export function isValidCarrierTracking(tracking?: string): boolean {
  if (!tracking || !tracking.trim()) return false;
  const t = tracking.trim();
  if (t.includes('Unshipped') || t.includes('Awaiting') || t.includes('Pending') || t.length < 8) {
    return false;
  }
  return true;
}

export const OrdersView: React.FC<OrdersViewProps> = ({
  active,
  products,
  sellerId,
  onSelectProductAndView,
  onOrdersCountChange
}) => {
  const [orders, setOrders] = useState<AutoOrderRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncingAmazon, setSyncingAmazon] = useState<boolean>(false);
  const [dispatchingCj, setDispatchingCj] = useState<boolean>(false);
  const [savingOrder, setSavingOrder] = useState<boolean>(false);
  const [syncingTracking, setSyncingTracking] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [liveMeta, setLiveMeta] = useState<{
    amazonConnected: boolean;
    cjConnected: boolean;
    amazonLiveCheckedAt: string | null;
    cjLiveCheckedAt: string | null;
    amazonOrderCount: number;
    cjOrderCount: number;
    sellerId: string;
    marketplaceId: string;
    cjOpenId: string;
  }>({
    amazonConnected: true,
    cjConnected: true,
    amazonLiveCheckedAt: null,
    cjLiveCheckedAt: null,
    amazonOrderCount: 0,
    cjOrderCount: 0,
    sellerId,
    marketplaceId: 'ATVPDKIKX0DER',
    cjOpenId: '57878'
  });

  // Editable draft state for the currently selected order in the right-hand inspector
  const [editDraft, setEditDraft] = useState<Partial<AutoOrderRecord>>({});

  // Add Additional Item to Order modal state
  const [addItemModalOrder, setAddItemModalOrder] = useState<AutoOrderRecord | null>(null);
  const [addItemForm, setAddItemForm] = useState<{
    mode: 'catalog' | 'custom';
    productId: string;
    productSearch: string;
    quantity: number;
    pricingMode: 'standard_addon' | 'free_bundle';
    customTitle: string;
    customSku: string;
    customCjSku: string;
    customUnitRetail: number;
    customUnitCost: number;
  }>({
    mode: 'catalog',
    productId: products[0]?.id || 'fba-prod-01',
    productSearch: '',
    quantity: 1,
    pricingMode: 'standard_addon',
    customTitle: '',
    customSku: '',
    customCjSku: '',
    customUnitRetail: 29.99,
    customUnitCost: 11.5
  });

  // New order modal state
  const [newOrderForm, setNewOrderForm] = useState<{
    productId: string;
    productSearch: string;
    quantity: number;
    channel: string;
    orderStatus: OrderStatus;
    paymentStatus: PaymentStatus;
    customerOrderNum: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerLocation: string;
    carrier: string;
    trackingNumber: string;
    usWarehouseLocation: string;
    estimatedDelivery: string;
    notes: string;
  }>({
    productId: products[0]?.id || 'fba-prod-01',
    productSearch: '',
    quantity: 1,
    channel: 'Amazon.com (MFN · StandardOrder)',
    orderStatus: 'Pending',
    paymentStatus: 'Paid',
    customerOrderNum: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerLocation: 'CARMEL, IN, US',
    carrier: 'UPS® Ground',
    trackingNumber: '',
    usWarehouseLocation: 'AiSmart LLC · GASTONIA, NC 28056',
    estimatedDelivery: '2026-11-04 to 2026-11-05',
    notes: ''
  });

  const loadOrders = useCallback(
    async (forceRefresh = false) => {
      setLoading(true);
      try {
        const res = await fetch(`/api/orders${forceRefresh ? '?refresh=1' : ''}`);
        if (res.ok) {
          const data = await res.json();
          if (data.meta) {
            setLiveMeta(data.meta);
          }
          if (Array.isArray(data.orders)) {
            const realOnly = data.orders.filter(
              (o: AutoOrderRecord) =>
                !String(o.customerOrderNum || o.id || '').startsWith('TEST-') &&
                String(o.orderStatus || '') !== 'TRASH'
            );
            setOrders(realOnly);
            onOrdersCountChange?.(realOnly.length);
            setSelectedOrderId((prev) => {
              if (prev && realOnly.some((o: AutoOrderRecord) => o.id === prev)) return prev;
              return '';
            });
          }
          return data;
        }
      } catch (_) {
      } finally {
        setLoading(false);
      }
      return null;
    },
    [onOrdersCountChange]
  );

  useEffect(() => {
    loadOrders(false);
  }, [loadOrders]);

  useEffect(() => {
    if (active) {
      loadOrders(false);
    }
  }, [active, loadOrders]);

  const selectedOrder = useMemo(
    () => (selectedOrderId ? orders.find((o) => o.id === selectedOrderId) || null : null),
    [orders, selectedOrderId]
  );

  const drawerDialogRef = useRef<HTMLDialogElement | null>(null);
  const pressedOutsideDrawerRef = useRef<boolean>(false);
  const addItemDialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dlg = drawerDialogRef.current;
    if (!dlg) return;
    if (selectedOrder) {
      if (!dlg.open) {
        try {
          dlg.showModal();
        } catch (_) {}
      }
    } else if (dlg.open) {
      try {
        dlg.close();
      } catch (_) {}
    }
  }, [selectedOrder]);

  useEffect(() => {
    const dlg = addItemDialogRef.current;
    if (!dlg) return;
    if (addItemModalOrder) {
      if (!dlg.open) {
        try {
          dlg.showModal();
        } catch (_) {}
      }
    } else if (dlg.open) {
      try {
        dlg.close();
      } catch (_) {}
    }
  }, [addItemModalOrder]);

  // Check if current recipient name or street address is placeholder/missing
  const currentRecipientName = (editDraft.customerName ?? selectedOrder?.customerName ?? '').trim();
  const currentRecipientAddress = (editDraft.customerLocation ?? selectedOrder?.customerLocation ?? '').trim();
  const nameHasIssue = isPlaceholderCustomerName(currentRecipientName);
  const addrHasIssue = isPlaceholderCustomerAddress(currentRecipientAddress);
  const hasAddressIssue = nameHasIssue || addrHasIssue;
  const hasExistingCjOrder = Boolean(
    (selectedOrder?.supplierOrderId && (selectedOrder.supplierOrderId.startsWith('SD') || selectedOrder.supplierOrderId.startsWith('CJ-') || selectedOrder.supplierOrderId.length > 5)) ||
    selectedOrder?.cjOrderStatus === 'CREATED' ||
    (editDraft?.supplierOrderId && (editDraft.supplierOrderId.startsWith('SD') || editDraft.supplierOrderId.length > 5))
  );

  // Sync inspector draft whenever selectedOrder changes
  useEffect(() => {
    if (selectedOrder) {
      setEditDraft({ ...selectedOrder });
    }
  }, [selectedOrder]);

  const handlePullLiveAmazonOrders = async () => {
    setSyncingAmazon(true);
    try {
      const data = await loadOrders(true);
      const amzCount = data?.meta?.amazonOrderCount ?? orders.length;
      const cjCount = data?.meta?.cjOrderCount ?? 0;
      showToast(
        `Live Sync Complete · Amazon SP-API (/orders/v0/orders): ${amzCount} real order(s) · CJ API: ${cjCount} order(s).`
      );
    } catch (_) {
      showToast('Refreshed live Order Desk.');
    } finally {
      setSyncingAmazon(false);
    }
  };

  const handleDispatchCjOrder = async () => {
    if (!selectedOrder) return;
    const nameToShip = (editDraft.customerName || selectedOrder.customerName || '').trim();
    const addrToShip = (editDraft.customerLocation || selectedOrder.customerLocation || '').trim();

    if (isPlaceholderCustomerName(nameToShip) || isPlaceholderCustomerAddress(addrToShip)) {
      showToast('⚠️ Dispatch Blocked: Please enter the real recipient name and full street address from Seller Central before dispatching to CJ.');
      return;
    }

    await handleSaveOrderUpdate();
  };

  const handleSyncTrackingToAmazon = async () => {
    if (!selectedOrder) return;
    const tracking = String(editDraft.trackingNumber || selectedOrder.trackingNumber || '').trim();
    const carrier = String(editDraft.carrier || selectedOrder.carrier || 'USPS').trim();

    if (!isValidCarrierTracking(tracking)) {
      showToast('⚠️ Action Blocked: A valid carrier tracking number is required to confirm shipment on Amazon.');
      return;
    }

    setSyncingTracking(true);
    try {
      const res = await fetch('/api/orders/sync-tracking-amazon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedOrder.id,
          customerOrderNum: selectedOrder.customerOrderNum,
          trackingNumber: tracking,
          carrier
        })
      });
      const data = await res.json();
      if (Array.isArray(data.orders)) {
        setOrders(data.orders);
      }
      if (data.order) {
        setEditDraft({ ...data.order });
        setSelectedOrderId(data.order.id);
      }
      if (data.ok) {
        showToast(`✓ Confirmed shipment & tracking ${tracking} live on Amazon Seller Central!`);
      } else {
        showToast(`Amazon SP-API Error: ${data.error || 'Failed to sync tracking to Amazon.'}`);
      }
    } catch (_) {
      showToast('Error communicating with Amazon SP-API.');
    } finally {
      setSyncingTracking(false);
    }
  };

  const handleSaveOrderUpdate = async (overrides?: Partial<AutoOrderRecord>, customToast?: string) => {
    if (!selectedOrder) return;
    const payload = {
      ...editDraft,
      ...(overrides || {}),
      id: selectedOrder.id,
      syncWithCj: true
    };
    setSavingOrder(true);
    setDispatchingCj(true);
    try {
      const res = await fetch('/api/orders/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
          onOrdersCountChange?.(data.orders.length);
        }
        if (data.order) {
          setEditDraft({ ...data.order });
          setSelectedOrderId(data.order.id);
        }
        if (data.cjUpdated) {
          showToast(customToast || `✓ Saved order & updated CJ Dropshipping (${data.order?.supplierOrderId || selectedOrder.customerOrderNum})!`);
        } else if (data.cjSync && !data.cjSync.success) {
          showToast(`Order saved locally. CJ notice: ${data.cjSync.message || data.cjSync.error || 'Check CJ details.'}`);
        } else {
          showToast(customToast || `✓ Saved order updates for ${selectedOrder.customerOrderNum}`);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        showToast(errData.error || 'Failed to update order.');
      }
    } catch (_) {
      showToast('Error saving order changes.');
    } finally {
      setSavingOrder(false);
      setDispatchingCj(false);
    }
  };

  const handleQuickRowStatusChange = async (order: AutoOrderRecord, nextStatus: OrderStatus) => {
    try {
      const res = await fetch('/api/orders/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: order.id,
          orderStatus: nextStatus
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
        }
        showToast(`Order ${order.customerOrderNum} marked as ${nextStatus}`);
      }
    } catch (_) {
      showToast('Could not update order status.');
    }
  };

  const handleBatchUpdate = async (updates: Partial<AutoOrderRecord>, label: string) => {
    if (!selectedIds.length) return;
    try {
      const res = await fetch('/api/orders/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderIds: selectedIds,
          updates
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
        }
        showToast(`Updated ${selectedIds.length} order(s): ${label}`);
        setSelectedIds([]);
      }
    } catch (_) {
      showToast('Failed to run batch update.');
    }
  };

  const handleDeleteOrder = async (order: AutoOrderRecord) => {
    try {
      const res = await fetch('/api/orders/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: order.id })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
          onOrdersCountChange?.(data.orders.length);
          if (selectedOrderId === order.id) {
            setSelectedOrderId(data.orders[0]?.id || '');
          }
        }
        showToast(`Removed order ${order.customerOrderNum}`);
      }
    } catch (_) {
      showToast('Could not remove order.');
    }
  };

  const openAddItemModal = (order: AutoOrderRecord) => {
    const defaultProd = products.find((p) => p.id !== order.productId) || products[0];
    setAddItemModalOrder(order);
    setAddItemForm((prev) => ({
      ...prev,
      mode: 'catalog',
      productId: defaultProd?.id || prev.productId,
      productSearch: '',
      quantity: 1,
      pricingMode: 'standard_addon'
    }));
  };

  const handleAddAdditionalItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addItemModalOrder) return;
    const targetOrder = orders.find((o) => o.id === addItemModalOrder.id) || addItemModalOrder;
    const existingAddl: OrderLineItem[] = Array.isArray(targetOrder.additionalItems)
      ? [...targetOrder.additionalItems]
      : [];
    const primaryQty = Math.max(
      1,
      Number(
        targetOrder.primaryQuantity ||
          Math.max(
            1,
            Number(targetOrder.quantity || 1) - existingAddl.reduce((s, it) => s + Number(it.quantity || 1), 0)
          )
      )
    );
    const addQty = Math.max(1, Number(addItemForm.quantity || 1));

    let newItem: OrderLineItem;
    if (addItemForm.mode === 'catalog') {
      const prod = products.find((p) => p.id === addItemForm.productId) || products[0];
      const baseRetail = Number(prod?.pricing?.amazonRetailPrice || 34.99);
      const unitRetail = addItemForm.pricingMode === 'free_bundle' ? 0 : baseRetail;
      const unitCost = Number(
        prod?.researchDossier?.usDropshipCost || prod?.pricing?.alibabaFobUnit || 12.5
      );
      newItem = {
        id: `item-${Date.now()}`,
        productId: prod?.id || 'fba-prod-01',
        productTitle: prod?.title || 'Catalog Product',
        productImage: prod?.images?.[0] || '/images/fba-prod-01-1.jpg',
        asin: prod?.amazonListing?.asin || prod?.asin || '',
        sku: prod?.amazonListing?.amazonSku || prod?.amazonListing?.genericSku || prod?.sku || '',
        cjSku:
          prod?.researchDossier?.cjSku ||
          prod?.researchDossier?.usDropshipSku ||
          (prod as any)?.cjSupplier?.cjSku ||
          '',
        cjPid: (prod as any)?.cjSupplier?.pid || '',
        quantity: addQty,
        unitRetail: +unitRetail.toFixed(2),
        unitCost: +unitCost.toFixed(2)
      };
    } else {
      const unitRetail = addItemForm.pricingMode === 'free_bundle' ? 0 : Number(addItemForm.customUnitRetail || 0);
      const unitCost = Number(addItemForm.customUnitCost || 0);
      newItem = {
        id: `item-${Date.now()}`,
        productId: 'custom-item',
        productTitle: addItemForm.customTitle.trim() || 'Additional Custom Order Item',
        productImage: '/images/fba-prod-01-1.jpg',
        sku: addItemForm.customSku.trim() || 'CUSTOM-SKU',
        cjSku: addItemForm.customCjSku.trim() || '',
        quantity: addQty,
        unitRetail: +unitRetail.toFixed(2),
        unitCost: +unitCost.toFixed(2)
      };
    }

    const nextAddl = [...existingAddl, newItem];
    const nextTotalQty = primaryQty + nextAddl.reduce((s, it) => s + Number(it.quantity || 1), 0);
    const nextRetail = +(Number(targetOrder.retailCollected || 0) + newItem.unitRetail * addQty).toFixed(2);
    const nextCost = +(Number(targetOrder.usSupplierCostCharged || 0) + newItem.unitCost * addQty).toFixed(2);

    setSavingOrder(true);
    try {
      const res = await fetch('/api/orders/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: targetOrder.id,
          primaryQuantity: primaryQty,
          additionalItems: nextAddl,
          quantity: nextTotalQty,
          retailCollected: nextRetail,
          usSupplierCostCharged: nextCost,
          syncWithCj: true,
          timelineNote: `Added ${addQty}x ${newItem.productTitle.slice(0, 52)} (${newItem.sku || newItem.cjSku || newItem.productId}) to order ${targetOrder.customerOrderNum}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
          onOrdersCountChange?.(data.orders.length);
        }
        if (data.order && selectedOrderId === data.order.id) {
          setEditDraft({ ...data.order });
        }
        setAddItemModalOrder(null);
        showToast(
          `✓ Added ${addQty}x ${newItem.productTitle.slice(0, 38)}… to Order ${targetOrder.customerOrderNum}${data.cjUpdated ? ' & synced with CJ Dropshipping!' : ''}`
        );
      } else {
        showToast('Failed to add item to order.');
      }
    } catch (_) {
      showToast('Error adding item to order.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleRemoveAdditionalItem = async (order: AutoOrderRecord, itemId: string) => {
    const existingAddl: OrderLineItem[] = Array.isArray(order.additionalItems) ? order.additionalItems : [];
    const targetItem = existingAddl.find((it) => it.id === itemId);
    if (!targetItem) return;

    const nextAddl = existingAddl.filter((it) => it.id !== itemId);
    const primaryQty = Math.max(
      1,
      Number(
        order.primaryQuantity ||
          Math.max(1, Number(order.quantity || 1) - existingAddl.reduce((s, it) => s + Number(it.quantity || 1), 0))
      )
    );
    const nextTotalQty = primaryQty + nextAddl.reduce((s, it) => s + Number(it.quantity || 1), 0);
    const removedRetail = Number(targetItem.unitRetail || 0) * Math.max(1, Number(targetItem.quantity || 1));
    const removedCost = Number(targetItem.unitCost || 0) * Math.max(1, Number(targetItem.quantity || 1));
    const nextRetail = Math.max(0, +(Number(order.retailCollected || 0) - removedRetail).toFixed(2));
    const nextCost = Math.max(0, +(Number(order.usSupplierCostCharged || 0) - removedCost).toFixed(2));

    setSavingOrder(true);
    try {
      const res = await fetch('/api/orders/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: order.id,
          primaryQuantity: primaryQty,
          additionalItems: nextAddl,
          quantity: nextTotalQty,
          retailCollected: nextRetail,
          usSupplierCostCharged: nextCost,
          syncWithCj: true,
          timelineNote: `Removed additional item ${targetItem.productTitle.slice(0, 48)} from order ${order.customerOrderNum}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
        }
        if (data.order && selectedOrderId === data.order.id) {
          setEditDraft({ ...data.order });
        }
        showToast(`Removed ${targetItem.productTitle.slice(0, 36)}… from Order ${order.customerOrderNum}`);
      }
    } catch (_) {
      showToast('Failed to remove additional item.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleUpdateAdditionalItemQty = async (order: AutoOrderRecord, itemId: string, nextItemQty: number) => {
    if (nextItemQty < 1) {
      return handleRemoveAdditionalItem(order, itemId);
    }
    const existingAddl: OrderLineItem[] = Array.isArray(order.additionalItems) ? order.additionalItems : [];
    const targetItem = existingAddl.find((it) => it.id === itemId);
    if (!targetItem) return;

    const deltaQty = nextItemQty - Math.max(1, Number(targetItem.quantity || 1));
    if (deltaQty === 0) return;

    const nextAddl = existingAddl.map((it) => (it.id === itemId ? { ...it, quantity: nextItemQty } : it));
    const primaryQty = Math.max(
      1,
      Number(
        order.primaryQuantity ||
          Math.max(1, Number(order.quantity || 1) - existingAddl.reduce((s, it) => s + Number(it.quantity || 1), 0))
      )
    );
    const nextTotalQty = primaryQty + nextAddl.reduce((s, it) => s + Number(it.quantity || 1), 0);
    const nextRetail = Math.max(
      0,
      +(Number(order.retailCollected || 0) + Number(targetItem.unitRetail || 0) * deltaQty).toFixed(2)
    );
    const nextCost = Math.max(
      0,
      +(Number(order.usSupplierCostCharged || 0) + Number(targetItem.unitCost || 0) * deltaQty).toFixed(2)
    );

    try {
      const res = await fetch('/api/orders/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: order.id,
          primaryQuantity: primaryQty,
          additionalItems: nextAddl,
          quantity: nextTotalQty,
          retailCollected: nextRetail,
          usSupplierCostCharged: nextCost,
          syncWithCj: true
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
        }
        if (data.order && selectedOrderId === data.order.id) {
          setEditDraft({ ...data.order });
        }
        showToast(`Updated item quantity to ${nextItemQty} on Order ${order.customerOrderNum}`);
      }
    } catch (_) {
      showToast('Could not update item quantity.');
    }
  };

  const handleCreateOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingOrder(true);
    try {
      const res = await fetch('/api/orders/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrderForm)
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.orders)) {
          setOrders(data.orders);
          onOrdersCountChange?.(data.orders.length);
        }
        if (data.order) {
          setSelectedOrderId(data.order.id);
        }
        setShowCreateModal(false);
        showToast(`Created order ${data.order?.customerOrderNum || ''} and routed to ${data.order?.usWarehouseLocation || 'US Warehouse'}`);
      }
    } catch (_) {
      showToast('Failed to create order.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleExportCsv = () => {
    const headers = [
      'Supplier Order ID',
      'Customer Order Number',
      'Order Status',
      'Payment Status',
      'Channel',
      'Product ID',
      'ASIN',
      'SKU',
      'CJ SKU',
      'Product Title',
      'Quantity',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Shipping Destination',
      'Warehouse',
      'Carrier',
      'Tracking Number',
      'Estimated Delivery',
      'Retail Collected ($)',
      'Supplier Cost ($)',
      'Referral Fee ($)',
      'Net Profit ($)',
      'Created At',
      'Notes'
    ];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = orders.map((o) =>
      [
        o.supplierOrderId,
        o.customerOrderNum,
        o.orderStatus || 'Dispatched',
        o.paymentStatus || 'Paid',
        o.channel,
        o.productId,
        o.asin || '',
        o.sku || '',
        o.cjSku || o.usSupplierSku || '',
        o.productTitle,
        o.quantity,
        o.customerName || '',
        o.customerEmail || '',
        o.customerPhone || '',
        o.customerLocation,
        o.usWarehouseLocation,
        o.carrier,
        o.trackingNumber,
        o.estimatedDelivery || '',
        o.retailCollected.toFixed(2),
        o.usSupplierCostCharged.toFixed(2),
        (o.amazonReferralFee || 0).toFixed(2),
        o.netProfitLocked.toFixed(2),
        o.createdAt || '',
        o.notes || ''
      ]
        .map(esc)
        .join(',')
    );
    const csvContent = `${headers.map(esc).join(',')}\n${rows.join('\n')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aurelius-orders-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${orders.length} orders to CSV.`);
  };

  // Filtered orders & KPIs
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: orders.length };
    for (const o of orders) {
      const st = o.orderStatus || 'Dispatched';
      counts[st] = (counts[st] || 0) + 1;
    }
    return counts;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const st = o.orderStatus || 'Dispatched';
      if (statusFilter !== 'all' && st !== statusFilter) return false;
      if (channelFilter !== 'all' && !o.channel.toLowerCase().includes(channelFilter.toLowerCase())) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const hay = [
          o.id,
          o.supplierOrderId,
          o.customerOrderNum,
          o.productTitle,
          o.productId,
          o.asin,
          o.sku,
          o.cjSku,
          o.usSupplierSku,
          o.customerName,
          o.customerEmail,
          o.customerLocation,
          o.trackingNumber,
          o.carrier,
          o.notes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, statusFilter, channelFilter, searchQuery]);

  const kpis = useMemo(() => {
    const activeOrders = orders.filter((o) => o.orderStatus !== 'Cancelled' && o.orderStatus !== 'Refunded');
    const totalUnits = activeOrders.reduce((s, o) => s + Number(o.quantity || 1), 0);
    const totalRevenue = activeOrders.reduce((s, o) => s + Number(o.retailCollected || 0), 0);
    const totalSupplierCost = activeOrders.reduce((s, o) => s + Number(o.usSupplierCostCharged || 0), 0);
    const totalReferralFees = activeOrders.reduce((s, o) => s + Number(o.amazonReferralFee || 0), 0);
    const totalNetProfit = activeOrders.reduce((s, o) => s + Number(o.netProfitLocked || 0), 0);
    const avgMarginPct = totalRevenue > 0 ? (totalNetProfit / totalRevenue) * 100 : 0;
    const pendingOrProcessing = orders.filter((o) => o.orderStatus === 'Pending' || o.orderStatus === 'Processing').length;
    const inTransitOrDispatched = orders.filter((o) => o.orderStatus === 'Dispatched' || o.orderStatus === 'In Transit').length;
    const deliveredCount = orders.filter((o) => o.orderStatus === 'Delivered').length;
    return {
      totalOrders: orders.length,
      activeCount: activeOrders.length,
      totalUnits,
      totalRevenue,
      totalSupplierCost,
      totalReferralFees,
      totalNetProfit,
      avgMarginPct,
      pendingOrProcessing,
      inTransitOrDispatched,
      deliveredCount
    };
  }, [orders]);

  const filteredModalProducts = useMemo(() => {
    const q = newOrderForm.productSearch.trim().toLowerCase();
    if (!q) return products.slice(0, 80);
    return products
      .filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q) ||
          (p.amazonListing?.amazonSku || '').toLowerCase().includes(q) ||
          (p.amazonListing?.asin || p.asin || '').toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [products, newOrderForm.productSearch]);

  const filteredAddItemProducts = useMemo(() => {
    const q = addItemForm.productSearch.trim().toLowerCase();
    if (!q) return products.slice(0, 80);
    return products
      .filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q) ||
          (p.amazonListing?.amazonSku || p.amazonListing?.genericSku || '').toLowerCase().includes(q) ||
          (p.amazonListing?.asin || p.asin || '').toLowerCase().includes(q) ||
          (p.researchDossier?.cjSku || p.researchDossier?.usDropshipSku || '').toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [products, addItemForm.productSearch]);

  if (!active) return null;

  // Live recalculated economics in the right-hand inspector draft
  const draftQty = Math.max(1, Number(editDraft.quantity ?? selectedOrder?.quantity ?? 1));
  const draftRetail = Number(editDraft.retailCollected ?? selectedOrder?.retailCollected ?? 0);
  const draftCost = Number(editDraft.usSupplierCostCharged ?? selectedOrder?.usSupplierCostCharged ?? 0);
  const draftChannel = String(editDraft.channel ?? selectedOrder?.channel ?? 'Amazon US');
  const draftRefRate = draftChannel.toLowerCase().includes('shopify')
    ? 0.029
    : draftChannel.toLowerCase().includes('tiktok')
    ? 0.06
    : 0.15;
  const draftRefFee = +(draftRetail * draftRefRate).toFixed(2);
  const draftNetProfit = +(draftRetail - draftCost - draftRefFee).toFixed(2);
  const currentStepperIdx = STEPPER_STAGES.indexOf((editDraft.orderStatus || selectedOrder?.orderStatus || 'Dispatched') as OrderStatus);

  return (
    <section
      id="view-orders"
      role="tabpanel"
      aria-labelledby="tab-orders"
      className="workspace-panel active"
      style={{ paddingBottom: 72 }}
    >
      <div style={{ width: '100%', maxWidth: '100%', padding: '0 28px', boxSizing: 'border-box' }}>
        {/* JET-BLACK ARCHITECTURAL SPLIT HERO */}
        <div
          style={{
            background: '#09090B',
            color: '#FFFFFF',
            borderRadius: 16,
            padding: '32px 38px',
            marginBottom: 20,
            border: '1px solid #27272A',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
            gap: 28,
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: '#FAF6EC',
                  color: '#9E782F',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '4px 10px',
                  borderRadius: 999
                }}
              >
                04.1 · MULTI-CHANNEL ORDER DESK &amp; US 3PL FULFILLMENT
              </span>
              <span style={{ fontSize: 12, color: '#4ADE80', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                ● 100% Live API Data · Amazon SP-API ({sellerId}: {liveMeta.amazonOrderCount || orders.length} order{orders.length === 1 ? '' : 's'}) &amp; CJ API (OpenID {liveMeta.cjOpenId}: {liveMeta.cjOrderCount} orders)
                {liveMeta.amazonLiveCheckedAt
                  ? ` · Synced ${new Date(liveMeta.amazonLiveCheckedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                  : ''}
              </span>
            </div>
            <h1
              style={{
                fontSize: 'clamp(26px, 2.6vw, 34px)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                margin: '0 0 10px 0',
                color: '#FFFFFF'
              }}
            >
              Live Customer Orders &amp;{' '}
              <span style={{ color: '#D9B668' }}>US Warehouse Fulfillment Control</span>
            </h1>
            <p style={{ color: '#D4D4D8', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px 0', maxWidth: 660 }}>
              Inspect every customer order across Amazon US, Shopify DTC, Walmart WFS, and TikTok Shop. Update order
              statuses, carrier tracking numbers, shipping addresses, and unit economics inline with automatic audit
              logging.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                style={{
                  background: '#FFFFFF',
                  color: '#09090B',
                  border: 'none',
                  borderRadius: 8,
                  padding: '11px 18px',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                + Create / Log New Order
              </button>
              <a
                href="https://cjdropshipping.com/myCJ.html#/directOrders"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #D9B668 0%, #B89230 100%)',
                  color: '#09090B',
                  border: 'none',
                  borderRadius: 8,
                  padding: '11px 18px',
                  fontSize: 13,
                  fontWeight: 800,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 44,
                  boxShadow: '0 2px 8px rgba(217, 182, 104, 0.25)'
                }}
                title="Open CJDropshipping Orders Dashboard to review and finalize orders"
              >
                <span>📦</span>
                <span>Open CJ Orders Dashboard ↗</span>
              </a>
              <button
                type="button"
                onClick={handlePullLiveAmazonOrders}
                disabled={syncingAmazon}
                style={{
                  background: '#18181B',
                  color: '#D9B668',
                  border: '1px solid #D9B668',
                  borderRadius: 8,
                  padding: '11px 18px',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                {syncingAmazon ? 'Polling Amazon SP-API...' : '↻ Sync Live Amazon SP-API Orders'}
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                style={{
                  background: '#18181B',
                  color: '#FFFFFF',
                  border: '1px solid #3F3F46',
                  borderRadius: 8,
                  padding: '11px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                ↓ Export Orders CSV
              </button>
            </div>
          </div>

          {/* RIGHT SUMMARY CARD IN HERO */}
          <div
            style={{
              background: '#18181B',
              border: '1px solid #27272A',
              borderRadius: 12,
              padding: 22,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #27272A',
                paddingBottom: 10,
                marginBottom: 14
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#D9B668', textTransform: 'uppercase' }}>
                LIVE ORDER LEDGER FINANCIALS
              </span>
              <span style={{ fontSize: 11, color: '#4ADE80', fontWeight: 700 }}>
                {kpis.activeCount} Active Orders ({kpis.totalUnits} Units)
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Gross Retail Collected
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                  {formatMoney(kpis.totalRevenue)}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                  Across {kpis.totalUnits} fulfilled unit(s)
                </div>
              </div>
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Net Profit Locked
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#4ADE80', marginTop: 4 }}>
                  +{formatMoney(kpis.totalNetProfit)}
                </div>
                <div style={{ fontSize: 11, color: '#D9B668', marginTop: 2 }}>
                  {kpis.avgMarginPct.toFixed(1)}% Net Margin After Fees
                </div>
              </div>
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  US Supplier &amp; 3PL Cost
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                  {formatMoney(kpis.totalSupplierCost)}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                  Referral Fees: {formatMoney(kpis.totalReferralFees)}
                </div>
              </div>
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  US Delivery Speed SLA
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                  2–4 Business Days
                </div>
                <div style={{ fontSize: 11, color: '#4ADE80', marginTop: 2 }}>
                  Ontario CA · Chino CA · NJ Hubs
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NUMBERED CONCIERGE KPI DOCK (01 - 04) */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            marginBottom: 20,
            overflow: 'hidden',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {[
            {
              num: '01',
              label: 'Total Orders Logged',
              val: `${kpis.totalOrders} Orders`,
              sub: `${kpis.totalUnits} total units ordered`,
              filterKey: 'all'
            },
            {
              num: '02',
              label: 'Pending / Processing',
              val: `${kpis.pendingOrProcessing} Needs Action`,
              sub: 'Click to filter orders awaiting scan',
              filterKey: 'Pending'
            },
            {
              num: '03',
              label: 'Dispatched & In Transit',
              val: `${kpis.inTransitOrDispatched} Active Shipments`,
              sub: 'Live USPS / UPS tracking assigned',
              filterKey: 'Dispatched'
            },
            {
              num: '04',
              label: 'Delivered & Settled',
              val: `${kpis.deliveredCount} Completed`,
              sub: `+${formatMoney(kpis.totalNetProfit)} net profit locked`,
              filterKey: 'Delivered'
            }
          ].map((item, idx) => (
            <button
              key={item.num}
              type="button"
              onClick={() => setStatusFilter(item.filterKey)}
              style={{
                textAlign: 'left',
                background: statusFilter === item.filterKey ? '#FAF6EC' : '#FFFFFF',
                border: 'none',
                borderRight: idx < 3 ? '1px solid #E4E4E7' : 'none',
                padding: '18px 22px',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', letterSpacing: '0.08em' }}>
                  {item.num} · {item.label.toUpperCase()}
                </span>
                {statusFilter === item.filterKey && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F' }}>● ACTIVE</span>
                )}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#09090B', letterSpacing: '-0.02em' }}>
                {item.val}
              </div>
              <div style={{ fontSize: 12, color: '#71717A', marginTop: 3 }}>{item.sub}</div>
            </button>
          ))}
        </div>

        {/* FILTER PILLS, CHANNEL SELECTOR, SEARCH & BATCH TOOLBAR */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[
                { key: 'all', label: 'All Orders' },
                { key: 'Pending', label: 'Pending' },
                { key: 'Processing', label: 'Processing' },
                { key: 'Dispatched', label: 'Dispatched' },
                { key: 'In Transit', label: 'In Transit' },
                { key: 'Delivered', label: 'Delivered' },
                { key: 'On Hold', label: 'On Hold' },
                { key: 'Cancelled', label: 'Cancelled' }
              ].map((pill) => {
                const count = statusCounts[pill.key] || 0;
                const isSelected = statusFilter === pill.key;
                return (
                  <button
                    key={pill.key}
                    type="button"
                    onClick={() => setStatusFilter(pill.key)}
                    style={{
                      background: isSelected ? '#09090B' : '#F8F8F9',
                      color: isSelected ? '#FFFFFF' : '#3F3F46',
                      border: isSelected ? '1px solid #09090B' : '1px solid #E4E4E7',
                      borderRadius: 999,
                      padding: '8px 14px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    <span>{pill.label}</span>
                    <span
                      style={{
                        background: isSelected ? '#D9B668' : '#E4E4E7',
                        color: '#09090B',
                        borderRadius: 999,
                        padding: '1px 7px',
                        fontSize: 11,
                        fontWeight: 800
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, flex: '1 1 340px', justifyContent: 'flex-end' }}>
              <select
                aria-label="Filter orders by channel"
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                style={{
                  height: 42,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: '1px solid #D4D4D8',
                  background: '#FFFFFF',
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#09090B'
                }}
              >
                <option value="all">All Channels ({orders.length})</option>
                <option value="amazon">Amazon US</option>
                <option value="shopify">Shopify DTC</option>
                <option value="walmart">Walmart WFS</option>
                <option value="tiktok">TikTok Shop</option>
                <option value="ebay">eBay Outlet</option>
              </select>

              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Order #, ASIN, SKU, Tracking, Customer, or City..."
                style={{
                  height: 42,
                  minWidth: 280,
                  flex: '1 1 260px',
                  maxWidth: 420,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: '1px solid #D4D4D8',
                  fontSize: 13,
                  color: '#09090B'
                }}
              />
            </div>
          </div>

          {/* BATCH UPDATE BAR WHEN CHECKBOXES ARE SELECTED */}
          {selectedIds.length > 0 && (
            <div
              style={{
                background: '#FAF6EC',
                border: '1px solid #D9B668',
                borderRadius: 10,
                padding: '10px 16px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                ✓ {selectedIds.length} order(s) selected for batch update:
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(['Processing', 'Dispatched', 'In Transit', 'Delivered', 'On Hold'] as OrderStatus[]).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => handleBatchUpdate({ orderStatus: st }, `Status → ${st}`)}
                    style={{
                      background: '#09090B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: 6,
                      padding: '7px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Mark {st}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  style={{
                    background: '#FFFFFF',
                    color: '#3F3F46',
                    border: '1px solid #D4D4D8',
                    borderRadius: 6,
                    padding: '7px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FULL-WIDTH ORDERS TABLE + RIGHT-HAND SLIDE-OVER DRAWER */}
        <div
          style={{
            width: '100%'
          }}
        >
          {/* SEMANTIC RESPONSIVE DATA TABLE */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 14,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #E4E4E7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12
              }}
            >
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  ACTIVE FULFILLMENT LEDGER
                </span>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#09090B', margin: '2px 0 0 0' }}>
                  Current Customer Orders ({filteredOrders.length})
                </h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <a
                  href="https://cjdropshipping.com/myCJ.html#/directOrders"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#09090B',
                    color: '#D9B668',
                    border: '1px solid #D9B668',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12,
                    fontWeight: 800,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Open CJDropshipping Orders Dashboard to finalize & fulfill orders"
                >
                  <span>📦</span>
                  <span>CJ Order Dashboard ↗</span>
                </a>
                <span style={{ fontSize: 12, color: '#71717A', fontWeight: 600 }}>
                  Click any order row to open the right-side Order Drawer →
                </span>
              </div>
            </div>

            <div
              tabIndex={0}
              role="region"
              aria-labelledby="orders-table-caption"
              className="orders-table-wrapper"
            >
              <table className="orders-data-table">
                <caption id="orders-table-caption" className="sr-only">
                  Current multi-channel customer orders and fulfillment status ledger
                </caption>
                <thead>
                  <tr>
                    <th style={{ width: 38 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all visible orders"
                        checked={
                          filteredOrders.length > 0 &&
                          filteredOrders.every((o) => selectedIds.includes(o.id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(filteredOrders.map((o) => o.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </th>
                    <th>Order # &amp; Date</th>
                    <th>Product &amp; Identifiers</th>
                    <th>Customer &amp; Channel</th>
                    <th>Status (Quick Update)</th>
                    <th>Carrier &amp; Tracking</th>
                    <th style={{ textAlign: 'right' }}>Financials</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 36, textAlign: 'center', color: '#71717A', fontSize: 14 }}>
                        No orders match your current filter criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => {
                      const isSelected = selectedOrder?.id === order.id;
                      const isChecked = selectedIds.includes(order.id);
                      const statusVal = (order.orderStatus || 'Dispatched') as OrderStatus;
                      const badgeStyle = getStatusBadgeStyle(statusVal);
                      const dateFormatted = order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'Oct 2026';

                      return (
                        <tr
                          key={order.id}
                          onClick={() => setSelectedOrderId(order.id)}
                          className={isSelected ? 'is-selected-row' : ''}
                          style={{
                            background: isSelected ? '#FAF6EC' : '#FFFFFF',
                            cursor: 'pointer',
                            borderBottom: '1px solid #E4E4E7'
                          }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Select order ${order.customerOrderNum}`}
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedIds((prev) => [...prev, order.id]);
                                } else {
                                  setSelectedIds((prev) => prev.filter((id) => id !== order.id));
                                }
                              }}
                            />
                          </td>
                          <th
                            scope="row"
                            style={{
                              background: isSelected ? '#FAF6EC' : '#FFFFFF',
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            <div style={{ fontWeight: 800, color: '#09090B', fontSize: 13 }}>
                              {order.customerOrderNum}
                            </div>
                            <div style={{ fontSize: 11, color: '#71717A', marginTop: 2 }}>
                              {order.supplierOrderId} · {dateFormatted}
                            </div>
                          </th>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <img
                                  src={order.productImage || '/images/fba-prod-01-main.jpg'}
                                  alt={order.productTitle}
                                  style={{
                                    width: 44,
                                    height: 44,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    border: '1px solid #E4E4E7',
                                    flexShrink: 0
                                  }}
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: '#09090B',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {order.productTitle}
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      alignItems: 'center',
                                      gap: 6,
                                      marginTop: 4,
                                      fontSize: 11,
                                      color: '#52525B',
                                      fontVariantNumeric: 'tabular-nums'
                                    }}
                                  >
                                    <span>
                                      Qty:{' '}
                                      <strong>
                                        {order.primaryQuantity ||
                                          Math.max(
                                            1,
                                            Number(order.quantity || 1) -
                                              (order.additionalItems || []).reduce((s, it) => s + Number(it.quantity || 1), 0)
                                          )}
                                      </strong>
                                      {(order.additionalItems?.length || 0) > 0 && (
                                        <span style={{ color: '#9E782F', fontWeight: 800 }}>
                                          {' '}
                                          ({order.quantity} total units)
                                        </span>
                                      )}
                                    </span>
                                    {order.sku && <span>· SKU: <code>{order.sku}</code></span>}
                                    {order.asin && <span>· ASIN: <code>{order.asin}</code></span>}
                                    {(order.cjPid || order.cjSku) && (
                                      <span
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 5,
                                          background: '#FAF6EC',
                                          border: '1px solid #E5D5B5',
                                          borderRadius: 6,
                                          padding: '2px 7px',
                                          color: '#09090B',
                                          fontWeight: 700
                                        }}
                                      >
                                        <span style={{ color: '#9E782F', fontWeight: 800 }}>CJ ID:</span>
                                        <code style={{ fontSize: 10.5 }}>{order.cjPid || order.cjSku}</code>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboardWithToast(
                                              order.cjPid || order.cjSku || '',
                                              'CJ Product ID'
                                            );
                                          }}
                                          style={{
                                            background: '#FFFFFF',
                                            color: '#09090B',
                                            border: '1px solid #D4D4D8',
                                            borderRadius: 4,
                                            padding: '1px 5px',
                                            fontSize: 10,
                                            fontWeight: 800,
                                            cursor: 'pointer'
                                          }}
                                          title="Copy CJ Dropshipping Product ID"
                                        >
                                          Copy
                                        </button>
                                        <a
                                          href={buildCjDirectProductUrl(
                                            order.cjPid,
                                            order.cjSku,
                                            order.usDropshipUrl,
                                            order.productTitle
                                          )}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            color: '#9E782F',
                                            fontWeight: 800,
                                            textDecoration: 'underline',
                                            fontSize: 10.5
                                          }}
                                          title="Open Live CJ US Warehouse Product Sheet (No EU Geo-Block)"
                                        >
                                          Open in CJ ↗
                                        </a>
                                        <a
                                          href="https://cjdropshipping.com/myCJ.html#/directOrders"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            color: '#065F46',
                                            fontWeight: 800,
                                            textDecoration: 'underline',
                                            fontSize: 10.5
                                          }}
                                          title="Open your created order in MyCJ Orders Dashboard"
                                        >
                                          MyCJ Order ↗
                                        </a>
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAddItemModal(order);
                                      }}
                                      style={{
                                        marginLeft: 4,
                                        background: '#09090B',
                                        color: '#D9B668',
                                        border: '1px solid #D9B668',
                                        borderRadius: 6,
                                        padding: '3px 9px',
                                        fontSize: 11,
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4
                                      }}
                                      title="Add an additional catalog or custom item to this order"
                                    >
                                      + Add Item
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* ADDITIONAL LINE ITEMS ON THIS ORDER ROW */}
                              {Array.isArray(order.additionalItems) && order.additionalItems.length > 0 && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6,
                                    paddingLeft: 10,
                                    borderLeft: '2px solid #D9B668'
                                  }}
                                >
                                  {order.additionalItems.map((item) => (
                                    <div
                                      key={item.id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        background: '#F8F8F9',
                                        border: '1px solid #E4E4E7',
                                        borderRadius: 8,
                                        padding: '6px 8px',
                                        fontVariantNumeric: 'tabular-nums'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                        <img
                                          src={item.productImage || '/images/fba-prod-01-1.jpg'}
                                          alt={item.productTitle}
                                          style={{
                                            width: 30,
                                            height: 30,
                                            objectFit: 'cover',
                                            borderRadius: 6,
                                            border: '1px solid #D4D4D8',
                                            flexShrink: 0
                                          }}
                                        />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 800,
                                              color: '#09090B',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis'
                                            }}
                                          >
                                            + {item.productTitle}
                                          </div>
                                          <div style={{ fontSize: 10, color: '#52525B' }}>
                                            {item.sku && <span>SKU: <code>{item.sku}</code> · </span>}
                                            <span>
                                              {item.unitRetail > 0 ? `+${formatMoney(item.unitRetail * item.quantity)}` : '$0.00 Bundle'} (Cost: {formatMoney(item.unitCost * item.quantity)})
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                        <button
                                          type="button"
                                          onClick={() => handleUpdateAdditionalItemQty(order, item.id, item.quantity - 1)}
                                          aria-label="Decrease additional item quantity"
                                          style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: 4,
                                            border: '1px solid #D4D4D8',
                                            background: '#FFFFFF',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            cursor: 'pointer'
                                          }}
                                        >
                                          −
                                        </button>
                                        <span style={{ fontSize: 11, fontWeight: 800, minWidth: 16, textAlign: 'center' }}>
                                          {item.quantity}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleUpdateAdditionalItemQty(order, item.id, item.quantity + 1)}
                                          aria-label="Increase additional item quantity"
                                          style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: 4,
                                            border: '1px solid #D4D4D8',
                                            background: '#FFFFFF',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            cursor: 'pointer'
                                          }}
                                        >
                                          +
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveAdditionalItem(order, item.id)}
                                          aria-label="Remove additional item from order"
                                          title="Remove this item from order"
                                          style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: 4,
                                            border: '1px solid #FECACA',
                                            background: '#FEF2F2',
                                            color: '#991B1B',
                                            fontSize: 11,
                                            fontWeight: 800,
                                            cursor: 'pointer',
                                            marginLeft: 2
                                          }}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#09090B' }}>
                              {order.customerName || 'Verified US Buyer'}
                            </div>
                            <div style={{ fontSize: 11, color: '#3F3F46', marginTop: 2 }}>
                              {order.customerLocation}
                            </div>
                            <div style={{ fontSize: 11, color: '#9E782F', fontWeight: 700, marginTop: 2 }}>
                              {order.channel}
                            </div>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              aria-label={`Change status for order ${order.customerOrderNum}`}
                              value={statusVal}
                              onChange={(e) => handleQuickRowStatusChange(order, e.target.value as OrderStatus)}
                              style={{
                                ...badgeStyle,
                                borderRadius: 999,
                                padding: '5px 10px',
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              {ORDER_STATUS_OPTIONS.map((st) => (
                                <option key={st} value={st}>
                                  {st}
                                </option>
                              ))}
                            </select>
                            <div style={{ fontSize: 10, color: '#71717A', marginTop: 4, fontWeight: 600 }}>
                              Payment: {order.paymentStatus || 'Paid'}
                            </div>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#09090B' }}>
                              {order.carrier}
                            </div>
                            {order.trackingNumber && !order.trackingNumber.toLowerCase().includes('pending') ? (
                              <a
                                href={buildCarrierTrackingUrl(order.carrier, order.trackingNumber)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: '#9E782F',
                                  textDecoration: 'underline',
                                  fontVariantNumeric: 'tabular-nums'
                                }}
                              >
                                {order.trackingNumber} ↗
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                                {order.trackingNumber || 'Pending Scan'}
                              </span>
                            )}
                            <div style={{ fontSize: 10, color: '#71717A', marginTop: 2 }}>
                              {order.usWarehouseLocation}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B' }} title="Gross Retail Collected">
                              {formatMoney(order.retailCollected)}
                            </div>
                            <div style={{ fontSize: 10.5, color: '#71717A' }} title="US Supplier Cost">
                              CJ Cost: {formatMoney(order.usSupplierCostCharged)}
                            </div>
                            <div style={{ fontSize: 10.5, color: '#DC2626', fontWeight: 600 }} title="Marketplace / Amazon Referral Fee">
                              Fee: -{formatMoney(order.amazonReferralFee ?? (order.retailCollected * 0.15))}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: order.netProfitLocked >= 0 ? '#065F46' : '#991B1B',
                                marginTop: 2,
                                borderTop: '1px dashed #E4E4E7',
                                paddingTop: 2
                              }}
                              title="True Net Profit after CJ Cost and Marketplace Referral Fees"
                            >
                              {order.netProfitLocked >= 0 ? '+' : ''}
                              {formatMoney(order.netProfitLocked)} Net
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT-SIDE SLIDE-OVER DRAWER FOR LIVE ORDER INSPECTOR & EDITOR */}
          <dialog
            ref={drawerDialogRef}
            {...({ closedby: 'any' } as any)}
            aria-labelledby="order-drawer-title"
            className="order-detail-drawer"
            onClose={() => setSelectedOrderId('')}
            onPointerDown={(e) => {
              if (addItemModalOrder) return;
              const dlg = drawerDialogRef.current;
              if (!dlg) return;
              const r = dlg.getBoundingClientRect();
              pressedOutsideDrawerRef.current =
                e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
            }}
            onPointerUp={(e) => {
              if (addItemModalOrder) return;
              const dlg = drawerDialogRef.current;
              if (!dlg || !pressedOutsideDrawerRef.current) return;
              const r = dlg.getBoundingClientRect();
              const isOutside =
                e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
              if (isOutside) {
                setSelectedOrderId('');
              }
            }}
          >
            {selectedOrder && (
              <>
                {/* STICKY DRAWER HEADER */}
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: '#FFFFFF',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    borderBottom: '1px solid #E4E4E7',
                    padding: '18px 24px'
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: '#9E782F',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      LIVE ORDER DOSSIER &amp; EDITOR · {selectedOrder.supplierOrderId}
                    </span>
                    <h3
                      id="order-drawer-title"
                      style={{
                        fontSize: 19,
                        fontWeight: 800,
                        color: '#09090B',
                        margin: '4px 0 0 0',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {selectedOrder.customerOrderNum}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        ...getStatusBadgeStyle((editDraft.orderStatus || selectedOrder.orderStatus || 'Dispatched') as OrderStatus),
                        padding: '5px 12px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800
                      }}
                    >
                      {editDraft.orderStatus || selectedOrder.orderStatus || 'Dispatched'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedOrderId('')}
                      aria-label="Close Order Dossier Drawer"
                      style={{
                        background: '#F8F8F9',
                        color: '#09090B',
                        border: '1px solid #D4D4D8',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: 'pointer',
                        minHeight: 38
                      }}
                    >
                      ✕ Close
                    </button>
                  </div>
                </div>

                {/* SCROLLABLE DRAWER BODY */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 18
                  }}
                >
                {/* INTERACTIVE 5-STAGE FULFILLMENT STEPPER */}
                <div
                  style={{
                    background: '#F8F8F9',
                    border: '1px solid #E4E4E7',
                    borderRadius: 10,
                    padding: 12
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: '#3F3F46',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      marginBottom: 8
                    }}
                  >
                    1-Click Fulfillment Stage Progression
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                    {STEPPER_STAGES.map((stage, idx) => {
                      const isDone = currentStepperIdx >= idx;
                      const isCurrent = (editDraft.orderStatus || selectedOrder.orderStatus) === stage;
                      return (
                        <button
                          key={stage}
                          type="button"
                          onClick={() => {
                            setEditDraft((prev) => ({ ...prev, orderStatus: stage }));
                            handleSaveOrderUpdate({ orderStatus: stage }, `Order ${selectedOrder.customerOrderNum} moved to ${stage}`);
                          }}
                          style={{
                            background: isCurrent ? '#09090B' : isDone ? '#FAF6EC' : '#FFFFFF',
                            color: isCurrent ? '#FFFFFF' : isDone ? '#9E782F' : '#71717A',
                            border: isCurrent ? '1px solid #09090B' : isDone ? '1px solid #D9B668' : '1px solid #E4E4E7',
                            borderRadius: 6,
                            padding: '7px 4px',
                            fontSize: 11,
                            fontWeight: 800,
                            cursor: 'pointer',
                            textAlign: 'center'
                          }}
                        >
                          {idx + 1}. {stage}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* PRODUCT CARD INSIDE INSPECTOR */}
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #E4E4E7',
                    background: '#FFFFFF'
                  }}
                >
                  <img
                    src={selectedOrder.productImage || '/images/fba-prod-01-main.jpg'}
                    alt={selectedOrder.productTitle}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #E4E4E7',
                      flexShrink: 0
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', lineHeight: 1.35 }}>
                      {selectedOrder.productTitle}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        marginTop: 5,
                        fontSize: 11,
                        color: '#52525B',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      <span>ID: <code>{selectedOrder.productId}</code></span>
                      {selectedOrder.asin && <span>· ASIN: <code>{selectedOrder.asin}</code></span>}
                      {selectedOrder.sku && <span>· SKU: <code>{selectedOrder.sku}</code></span>}
                    </div>

                    {/* CJ DROPSHIPPING PRODUCT ID, SKU, COPY BUTTONS & DIRECT OPEN LINK */}
                    <div
                      style={{
                        marginTop: 8,
                        padding: '8px 10px',
                        background: '#FAF6EC',
                        border: '1px solid #E5D5B5',
                        borderRadius: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          CJ Product ID:
                        </span>
                        <code
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: '#09090B',
                            background: '#FFFFFF',
                            border: '1px solid #E4E4E7',
                            borderRadius: 4,
                            padding: '2px 6px'
                          }}
                        >
                          {selectedOrder.cjPid || selectedOrder.cjSku || 'N/A'}
                        </code>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboardWithToast(
                              selectedOrder.cjPid || selectedOrder.cjSku || '',
                              'CJ Dropshipping Product ID'
                            )
                          }
                          style={{
                            background: '#09090B',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: 5,
                            padding: '3px 8px',
                            fontSize: 10.5,
                            fontWeight: 800,
                            cursor: 'pointer'
                          }}
                        >
                          Copy CJ ID
                        </button>

                        {selectedOrder.cjSku && (
                          <>
                            <span style={{ fontSize: 11, color: '#71717A' }}>·</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#3F3F46' }}>
                              CJ SKU: <code style={{ fontWeight: 800, color: '#09090B' }}>{selectedOrder.cjSku}</code>
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboardWithToast(selectedOrder.cjSku || '', 'CJ SKU')
                              }
                              style={{
                                background: '#FFFFFF',
                                color: '#09090B',
                                border: '1px solid #D4D4D8',
                                borderRadius: 5,
                                padding: '2px 7px',
                                fontSize: 10.5,
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              Copy SKU
                            </button>
                          </>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <a
                          href={buildCjDirectProductUrl(
                            selectedOrder.cjPid,
                            selectedOrder.cjSku,
                            selectedOrder.usDropshipUrl,
                            selectedOrder.productTitle
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#FFFFFF',
                            color: '#9E782F',
                            border: '1px solid #D9B668',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 800,
                            textDecoration: 'none'
                          }}
                        >
                          Open Live CJ US Product Sheet ↗
                        </a>
                        <a
                          href="https://cjdropshipping.com/myCJ.html#/directOrders"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#09090B',
                            color: '#D9B668',
                            border: '1px solid #D9B668',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 800,
                            textDecoration: 'none'
                          }}
                        >
                          Open Order in MyCJ ({selectedOrder.supplierOrderId || 'SD26102722101806431001'}) ↗
                        </a>
                        <a
                          href={buildCjSearchUrl(selectedOrder.cjSku, selectedOrder.cjPid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#FFFFFF',
                            color: '#09090B',
                            border: '1px solid #D4D4D8',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 700,
                            textDecoration: 'none'
                          }}
                        >
                          Search CJ SKU ↗
                        </a>
                      </div>

                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                      {selectedOrder.customerOrderNum && (
                        <a
                          href={`https://sellercentral.amazon.com/orders-v3/order/${encodeURIComponent(selectedOrder.customerOrderNum)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, fontWeight: 800, color: '#065F46', textDecoration: 'underline' }}
                        >
                          Seller Central Order ↗
                        </a>
                      )}
                      {selectedOrder.asin && (
                        <a
                          href={`https://www.amazon.com/dp/${selectedOrder.asin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textDecoration: 'underline' }}
                        >
                          Amazon Listing ↗
                        </a>
                      )}
                      <a
                        href={buildCjDirectProductUrl(
                          selectedOrder.cjPid,
                          selectedOrder.cjSku,
                          selectedOrder.usDropshipUrl,
                          selectedOrder.productTitle
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, fontWeight: 800, color: '#09090B', textDecoration: 'underline' }}
                      >
                        CJ US Supplier Page ↗
                      </a>
                      {onSelectProductAndView && (
                        <button
                          type="button"
                          onClick={() => onSelectProductAndView(selectedOrder.productId, 'products')}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontSize: 11,
                            fontWeight: 800,
                            color: '#3F3F46',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          View in Catalog →
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openAddItemModal(selectedOrder)}
                        style={{
                          background: '#09090B',
                          color: '#D9B668',
                          border: '1px solid #D9B668',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        + Add Additional Item to Order
                      </button>
                    </div>
                  </div>
                </div>

                {/* ADDITIONAL LINE ITEMS IN DRAWER */}
                {Array.isArray(selectedOrder.additionalItems) && selectedOrder.additionalItems.length > 0 && (
                  <div
                    style={{
                      background: '#F8F8F9',
                      border: '1px solid #D9B668',
                      borderRadius: 10,
                      padding: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        ADDITIONAL ORDER ITEMS ({selectedOrder.additionalItems.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => openAddItemModal(selectedOrder)}
                        style={{
                          background: '#09090B',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        + Add Another Item
                      </button>
                    </div>
                    {selectedOrder.additionalItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          background: '#FFFFFF',
                          border: '1px solid #E4E4E7',
                          borderRadius: 8,
                          padding: '8px 10px',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                          <img
                            src={item.productImage || '/images/fba-prod-01-1.jpg'}
                            alt={item.productTitle}
                            style={{
                              width: 38,
                              height: 38,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #E4E4E7',
                              flexShrink: 0
                            }}
                          />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#09090B', lineHeight: 1.3 }}>
                              {item.productTitle}
                            </div>
                            <div style={{ fontSize: 11, color: '#52525B', marginTop: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                              {item.sku && <span>SKU: <code>{item.sku}</code></span>}
                              {(item.cjPid || item.cjSku) && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: '#FAF6EC',
                                    border: '1px solid #E5D5B5',
                                    borderRadius: 5,
                                    padding: '1px 6px'
                                  }}
                                >
                                  <strong style={{ color: '#9E782F', fontSize: 10 }}>CJ ID:</strong>
                                  <code style={{ fontSize: 10.5, color: '#09090B', fontWeight: 800 }}>
                                    {item.cjPid || item.cjSku}
                                  </code>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      copyToClipboardWithToast(item.cjPid || item.cjSku || '', 'CJ Product ID')
                                    }
                                    style={{
                                      background: '#09090B',
                                      color: '#FFFFFF',
                                      border: 'none',
                                      borderRadius: 4,
                                      padding: '1px 5px',
                                      fontSize: 9.5,
                                      fontWeight: 800,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Copy
                                  </button>
                                  <a
                                    href={buildCjDirectProductUrl(item.cjPid, item.cjSku)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#9E782F',
                                      fontWeight: 800,
                                      textDecoration: 'underline',
                                      fontSize: 10
                                    }}
                                  >
                                    Open in CJ ↗
                                  </a>
                                </span>
                              )}
                              <span>
                                Retail: <strong>{formatMoney(item.unitRetail * item.quantity)}</strong> · CJ Cost: <strong>{formatMoney(item.unitCost * item.quantity)}</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => handleUpdateAdditionalItemQty(selectedOrder, item.id, item.quantity - 1)}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 6,
                              border: '1px solid #D4D4D8',
                              background: '#F8F8F9',
                              fontWeight: 800,
                              cursor: 'pointer'
                            }}
                          >
                            −
                          </button>
                          <span style={{ fontSize: 12, fontWeight: 800, minWidth: 18, textAlign: 'center' }}>
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateAdditionalItemQty(selectedOrder, item.id, item.quantity + 1)}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 6,
                              border: '1px solid #D4D4D8',
                              background: '#F8F8F9',
                              fontWeight: 800,
                              cursor: 'pointer'
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveAdditionalItem(selectedOrder, item.id)}
                            style={{
                              background: '#FEF2F2',
                              color: '#991B1B',
                              border: '1px solid #FECACA',
                              borderRadius: 6,
                              padding: '4px 8px',
                              fontSize: 11,
                              fontWeight: 800,
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* LIVE AMAZON SP-API ORDER TELEMETRY STRIP */}
                <div
                  style={{
                    background: '#FAF6EC',
                    border: '1px solid #E5D5B5',
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontSize: 11,
                    color: '#3F3F46',
                    fontVariantNumeric: 'tabular-nums',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '6px 12px'
                  }}
                >
                  <div>
                    <strong style={{ color: '#09090B' }}>SP-API Status:</strong>{' '}
                    <span style={{ fontWeight: 800, color: '#92400E' }}>
                      {selectedOrder.amazonRawStatus || 'Unshipped'} ({selectedOrder.fulfillmentChannel || 'MFN'})
                    </span>
                  </div>
                  <div>
                    <strong style={{ color: '#09090B' }}>OrderItemId:</strong>{' '}
                    <code>{selectedOrder.orderItemId || 'Live SP-API'}</code>
                  </div>
                  <div>
                    <strong style={{ color: '#09090B' }}>Amazon Checkout:</strong>{' '}
                    {formatMoney(selectedOrder.itemPrice ?? selectedOrder.retailCollected)} +{' '}
                    {formatMoney(selectedOrder.itemTax ?? 0)} tax ={' '}
                    <strong style={{ color: '#09090B' }}>
                      {formatMoney(selectedOrder.orderTotal ?? selectedOrder.retailCollected)}
                    </strong>
                  </div>
                  <div>
                    <strong style={{ color: '#09090B' }}>Must Ship Window:</strong>{' '}
                    {selectedOrder.earliestShipDate
                      ? `${selectedOrder.earliestShipDate.slice(0, 10)} → ${selectedOrder.latestShipDate?.slice(0, 10) || ''}`
                      : 'Standard MFN'}
                  </div>
                </div>

                {/* EDITABLE FORM SECTIONS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <div>
                    <label
                      htmlFor="ord-edit-status"
                      style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Order Status
                    </label>
                    <select
                      id="ord-edit-status"
                      value={editDraft.orderStatus || 'Pending'}
                      onChange={(e) => setEditDraft({ ...editDraft, orderStatus: e.target.value as OrderStatus })}
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 13,
                        fontWeight: 800,
                        color: '#09090B',
                        background: '#FFFFFF'
                      }}
                    >
                      {ORDER_STATUS_OPTIONS.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="ord-edit-payment"
                      style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Payment Status
                    </label>
                    <select
                      id="ord-edit-payment"
                      value={editDraft.paymentStatus || 'Paid'}
                      onChange={(e) => setEditDraft({ ...editDraft, paymentStatus: e.target.value as PaymentStatus })}
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#09090B',
                        background: '#FFFFFF'
                      }}
                    >
                      {PAYMENT_STATUS_OPTIONS.map((ps) => (
                        <option key={ps} value={ps}>
                          {ps}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="ord-edit-channel"
                      style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Sales Channel
                    </label>
                    <select
                      id="ord-edit-channel"
                      value={editDraft.channel || CHANNEL_OPTIONS[0]}
                      onChange={(e) => setEditDraft({ ...editDraft, channel: e.target.value })}
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#09090B',
                        background: '#FFFFFF'
                      }}
                    >
                      {CHANNEL_OPTIONS.map((ch) => (
                        <option key={ch} value={ch}>
                          {ch}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="ord-edit-eta"
                      style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Delivery Window
                    </label>
                    <input
                      id="ord-edit-eta"
                      type="text"
                      value={editDraft.estimatedDelivery || '2026-11-04 to 2026-11-05'}
                      onChange={(e) => setEditDraft({ ...editDraft, estimatedDelivery: e.target.value })}
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#09090B',
                        boxSizing: 'border-box',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    />
                  </div>
                </div>

                {/* SHIPPING, CARRIER & TRACKING SECTION */}
                <div
                  style={{
                    background: '#F8F8F9',
                    border: '1px solid #E4E4E7',
                    borderRadius: 10,
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      CARRIER, TRACKING &amp; US WAREHOUSE ROUTING
                    </span>
                    <span
                      style={{
                        background: '#FAF6EC',
                        color: '#9E782F',
                        border: '1px solid #D9B668',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 10,
                        fontWeight: 800
                      }}
                    >
                      ✓ CJ US Domestic Warehouse · USPS+
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                    <div>
                      <label
                        htmlFor="ord-edit-carrier"
                        style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                      >
                        Shipping Carrier
                      </label>
                      <select
                        id="ord-edit-carrier"
                        value={editDraft.carrier || CARRIER_OPTIONS[0]}
                        onChange={(e) => setEditDraft({ ...editDraft, carrier: e.target.value })}
                        style={{
                          width: '100%',
                          height: 38,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #D4D4D8',
                          fontSize: 12,
                          fontWeight: 700,
                          background: '#FFFFFF',
                          color: '#09090B'
                        }}
                      >
                        {CARRIER_OPTIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="ord-edit-warehouse"
                        style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                      >
                        US Dispatch Warehouse
                      </label>
                      <select
                        id="ord-edit-warehouse"
                        value={editDraft.usWarehouseLocation || WAREHOUSE_OPTIONS[0]}
                        onChange={(e) => setEditDraft({ ...editDraft, usWarehouseLocation: e.target.value })}
                        style={{
                          width: '100%',
                          height: 38,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #D4D4D8',
                          fontSize: 12,
                          fontWeight: 700,
                          background: '#FFFFFF',
                          color: '#09090B'
                        }}
                      >
                        {WAREHOUSE_OPTIONS.map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="ord-edit-tracking"
                      style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Tracking Number
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        id="ord-edit-tracking"
                        type="text"
                        value={editDraft.trackingNumber ?? ''}
                        onChange={(e) => setEditDraft({ ...editDraft, trackingNumber: e.target.value })}
                        placeholder="940011189956..."
                        style={{
                          flex: 1,
                          height: 38,
                          padding: '0 10px',
                          borderRadius: 6,
                          border: '1px solid #D4D4D8',
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#09090B',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(String(editDraft.trackingNumber || ''));
                          showToast('Copied tracking number to clipboard.');
                        }}
                        style={{
                          background: '#FFFFFF',
                          color: '#09090B',
                          border: '1px solid #D4D4D8',
                          borderRadius: 6,
                          padding: '0 10px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Copy
                      </button>
                      <a
                        href={buildCarrierTrackingUrl(String(editDraft.carrier || ''), String(editDraft.trackingNumber || ''))}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#09090B',
                          color: '#FFFFFF',
                          borderRadius: 6,
                          padding: '0 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          textDecoration: 'none'
                        }}
                      >
                        Track ↗
                      </a>
                      {selectedOrder?.customerOrderNum?.includes('-') && (
                        <button
                          type="button"
                          onClick={handleSyncTrackingToAmazon}
                          disabled={syncingTracking || !isValidCarrierTracking(editDraft.trackingNumber)}
                          style={{
                            background: selectedOrder.amazonShipmentConfirmed ? '#15803D' : '#09090B',
                            color: '#FFFFFF',
                            border: `1px solid ${selectedOrder.amazonShipmentConfirmed ? '#16A34A' : '#D9B668'}`,
                            borderRadius: 6,
                            padding: '0 12px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: syncingTracking || !isValidCarrierTracking(editDraft.trackingNumber) ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                          title={
                            selectedOrder.amazonShipmentConfirmed
                              ? 'Shipment & tracking already confirmed on Amazon Seller Central'
                              : 'Sync tracking number live to Amazon Seller Central'
                          }
                        >
                          {syncingTracking
                            ? 'Syncing...'
                            : selectedOrder.amazonShipmentConfirmed
                            ? '✓ Confirmed on Amazon'
                            : '⚡ Sync to Amazon'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* CUSTOMER & SHIPPING DESTINATION SECTION */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      CUSTOMER &amp; SHIPPING DESTINATION
                    </span>
                    {hasAddressIssue && selectedOrder && (
                      <a
                        href={`https://sellercentral.amazon.com/orders-v3/order/${selectedOrder.customerOrderNum}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#B45309',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Copy Address in Seller Central ↗
                      </a>
                    )}
                  </div>

                  {hasAddressIssue && selectedOrder && (
                    <div
                      style={{
                        background: '#FFFBEB',
                        border: '1px solid #FCD34D',
                        borderRadius: 8,
                        padding: '10px 12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 12, color: '#92400E' }}>
                        <span>⚠️ Real Street Address Required Before CJ Dispatch</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 11, color: '#78350F', lineHeight: 1.4 }}>
                        Amazon automatically masks customer names and street addresses via API to protect buyer privacy. 
                        Please copy the customer&apos;s real name and street address from{' '}
                        <a
                          href={`https://sellercentral.amazon.com/orders-v3/order/${selectedOrder.customerOrderNum}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#B45309', fontWeight: 800, textDecoration: 'underline' }}
                        >
                          Seller Central Order #{selectedOrder.customerOrderNum} ↗
                        </a>{' '}
                        and paste them below before dispatching to CJ Dropshipping.
                      </p>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                    <div>
                      <label
                        htmlFor="ord-edit-custname"
                        style={{ display: 'block', fontSize: 10, fontWeight: 700, color: nameHasIssue ? '#B45309' : '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                      >
                        Customer Name {nameHasIssue && <span style={{ color: '#DC2626' }}>* (Required)</span>}
                      </label>
                      <input
                        id="ord-edit-custname"
                        type="text"
                        placeholder="e.g. John Doe"
                        value={editDraft.customerName ?? ''}
                        onChange={(e) => setEditDraft({ ...editDraft, customerName: e.target.value })}
                        style={{
                          width: '100%',
                          height: 38,
                          padding: '0 10px',
                          borderRadius: 6,
                          border: `1px solid ${nameHasIssue ? '#F59E0B' : '#D4D4D8'}`,
                          background: nameHasIssue ? '#FFFBEB' : '#FFFFFF',
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#09090B',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="ord-edit-custphone"
                        style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                      >
                        Customer Phone
                      </label>
                      <input
                        id="ord-edit-custphone"
                        type="text"
                        placeholder="e.g. 317-555-0100"
                        value={editDraft.customerPhone ?? ''}
                        onChange={(e) => setEditDraft({ ...editDraft, customerPhone: e.target.value })}
                        style={{
                          width: '100%',
                          height: 38,
                          padding: '0 10px',
                          borderRadius: 6,
                          border: '1px solid #D4D4D8',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#09090B',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="ord-edit-custaddr"
                      style={{ display: 'block', fontSize: 10, fontWeight: 700, color: addrHasIssue ? '#B45309' : '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                    >
                      Shipping Street Address, City, State &amp; ZIP {addrHasIssue && <span style={{ color: '#DC2626' }}>* (Required)</span>}
                    </label>
                    <input
                      id="ord-edit-custaddr"
                      type="text"
                      placeholder="e.g. 123 Main St, Apt 4B, Carmel, IN 46032"
                      value={editDraft.customerLocation ?? ''}
                      onChange={(e) => setEditDraft({ ...editDraft, customerLocation: e.target.value })}
                      style={{
                        width: '100%',
                        height: 38,
                        padding: '0 10px',
                        borderRadius: 6,
                        border: `1px solid ${addrHasIssue ? '#F59E0B' : '#D4D4D8'}`,
                        background: addrHasIssue ? '#FFFBEB' : '#FFFFFF',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#09090B',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                {/* ORDER UNIT ECONOMICS & PROFIT ADJUSTMENT */}
                <div
                  style={{
                    background: '#09090B',
                    color: '#FFFFFF',
                    borderRadius: 10,
                    padding: 14,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#D9B668', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      ORDER ECONOMICS &amp; LIVE NET PROFIT
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: draftNetProfit >= 0 ? '#4ADE80' : '#F87171' }}>
                      Net Profit: {draftNetProfit >= 0 ? '+' : ''}{formatMoney(draftNetProfit)}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    <div>
                      <label
                        htmlFor="ord-edit-qty"
                        style={{ display: 'block', fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}
                      >
                        Quantity
                      </label>
                      <input
                        id="ord-edit-qty"
                        type="number"
                        min={1}
                        value={draftQty}
                        onChange={(e) => {
                          const q = Math.max(1, Number(e.target.value) || 1);
                          const unitRetail = draftRetail / draftQty;
                          const unitCost = draftCost / draftQty;
                          setEditDraft({
                            ...editDraft,
                            quantity: q,
                            retailCollected: +(unitRetail * q).toFixed(2),
                            usSupplierCostCharged: +(unitCost * q).toFixed(2)
                          });
                        }}
                        style={{
                          width: '100%',
                          height: 36,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #3F3F46',
                          background: '#18181B',
                          color: '#FFFFFF',
                          fontSize: 13,
                          fontWeight: 800,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="ord-edit-retail"
                        style={{ display: 'block', fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}
                      >
                        Retail Total ($)
                      </label>
                      <input
                        id="ord-edit-retail"
                        type="number"
                        step="0.01"
                        value={draftRetail}
                        onChange={(e) => setEditDraft({ ...editDraft, retailCollected: Number(e.target.value) || 0 })}
                        style={{
                          width: '100%',
                          height: 36,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #3F3F46',
                          background: '#18181B',
                          color: '#FFFFFF',
                          fontSize: 13,
                          fontWeight: 800,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="ord-edit-cost"
                        style={{ display: 'block', fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}
                      >
                        CJ Supplier ($)
                      </label>
                      <input
                        id="ord-edit-cost"
                        type="number"
                        step="0.01"
                        value={draftCost}
                        onChange={(e) => setEditDraft({ ...editDraft, usSupplierCostCharged: Number(e.target.value) || 0 })}
                        style={{
                          width: '100%',
                          height: 36,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #3F3F46',
                          background: '#18181B',
                          color: '#FFFFFF',
                          fontSize: 13,
                          fontWeight: 800,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="ord-edit-fee"
                        style={{ display: 'block', fontSize: 10, color: '#F87171', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}
                      >
                        Marketplace Fee
                      </label>
                      <input
                        id="ord-edit-fee"
                        type="text"
                        readOnly
                        value={`-$${draftRefFee.toFixed(2)} (${(draftRefRate * 100).toFixed(0)}%)`}
                        title="Amazon standard 15% referral fee deducted from customer price"
                        style={{
                          width: '100%',
                          height: 36,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid #3F3F46',
                          background: '#27272A',
                          color: '#F87171',
                          fontSize: 12,
                          fontWeight: 800,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* OPERATOR NOTES */}
                <div>
                  <label
                    htmlFor="ord-edit-notes"
                    style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                  >
                    Internal Operator &amp; Fulfillment Notes
                  </label>
                  <textarea
                    id="ord-edit-notes"
                    rows={2}
                    value={editDraft.notes ?? ''}
                    onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                    placeholder="Add warehouse instructions, customer requests, or return notes..."
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      fontSize: 12,
                      color: '#09090B',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* ORDER ACTIVITY TIMELINE */}
                <div style={{ borderTop: '1px solid #E4E4E7', paddingTop: 14 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: '#71717A',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 8
                    }}
                  >
                    Order Audit Timeline ({(selectedOrder.timeline || []).length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(selectedOrder.timeline || []).slice(0, 6).map((ev, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 11,
                          padding: '8px 10px',
                          background: '#F8F8F9',
                          borderRadius: 6,
                          borderLeft: '3px solid #9E782F'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#71717A', fontSize: 10, fontWeight: 700 }}>
                          <span>{ev.actor}</span>
                          <span className="tabular">{new Date(ev.at).toLocaleString()}</span>
                        </div>
                        <div style={{ color: '#09090B', fontWeight: 600, marginTop: 2 }}>{ev.event}</div>
                      </div>
                    ))}
                  </div>
                </div>
                </div>

                {/* STICKY DRAWER FOOTER: SINGLE UNIFIED SAVE & SYNC BUTTON */}
                <div
                  style={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10,
                    background: '#FFFFFF',
                    borderTop: '1px solid #E4E4E7',
                    padding: '14px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}
                >
                  {/* DIRECT HIGH-VISIBILITY BUTTON TO FINALIZE ON CJ */}
                  <a
                    href="https://cjdropshipping.com/myCJ.html#/directOrders"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #D9B668 0%, #B89230 100%)',
                      color: '#09090B',
                      border: 'none',
                      borderRadius: 8,
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 800,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      minHeight: 46,
                      boxShadow: '0 2px 10px rgba(217, 182, 104, 0.35)',
                      boxSizing: 'border-box'
                    }}
                    title="Open CJDropshipping Orders Dashboard to pay and finalize this order"
                  >
                    <span>📦</span>
                    <span>Go to CJ Orders Dashboard to Finalize &amp; Pay Order ↗</span>
                  </a>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => handleSaveOrderUpdate()}
                      disabled={savingOrder || dispatchingCj}
                      style={{
                        flex: 1,
                        background: '#09090B',
                        color: '#D9B668',
                        border: '1px solid #D9B668',
                        borderRadius: 8,
                        padding: '12px 16px',
                        fontSize: 13,
                        fontWeight: 800,
                        cursor: savingOrder || dispatchingCj ? 'not-allowed' : 'pointer',
                        minHeight: 44,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                    >
                      {savingOrder || dispatchingCj ? (
                        <>
                          <svg
                            style={{
                              width: 14,
                              height: 14,
                              animation: 'spin 1s linear infinite'
                            }}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                          <span>Saving &amp; Updating CJ Dropshipping...</span>
                        </>
                      ) : hasExistingCjOrder ? (
                        '⚡ Save Order & Update CJ Dropshipping'
                      ) : (
                        '⚡ Save Order & Dispatch to CJ Dropshipping'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteOrder(selectedOrder)}
                      style={{
                        background: '#FEF2F2',
                        color: '#991B1B',
                        border: '1px solid #FECACA',
                        borderRadius: 8,
                        padding: '12px 14px',
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: 'pointer',
                        minHeight: 44
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </dialog>
        </div>
      </div>

      {/* CREATE NEW ORDER MODAL */}
      {showCreateModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-order-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(9, 9, 11, 0.72)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF',
              borderRadius: 16,
              border: '1px solid #E4E4E7',
              maxWidth: 680,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: 28,
              boxShadow: '0 24px 48px rgba(0,0,0,0.28)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  NEW MULTI-CHANNEL ORDER DISPATCH
                </span>
                <h2 id="create-order-modal-title" style={{ fontSize: 22, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0' }}>
                  Create / Log Customer Order
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: '#F3F4F6',
                  border: 'none',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateOrderSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}>
                  Search &amp; Select Catalog Product ({products.length} SKUs)
                </label>
                <input
                  type="search"
                  value={newOrderForm.productSearch}
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, productSearch: e.target.value })}
                  placeholder="Filter catalog by title, SKU, or ASIN..."
                  style={{
                    width: '100%',
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #D4D4D8',
                    fontSize: 13,
                    marginBottom: 8,
                    boxSizing: 'border-box'
                  }}
                />
                <select
                  value={newOrderForm.productId}
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, productId: e.target.value })}
                  style={{
                    width: '100%',
                    height: 44,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #09090B',
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#09090B',
                    background: '#FAF6EC'
                  }}
                >
                  {filteredModalProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      #{p.rank} · {p.amazonListing?.amazonSku || p.id} · ${p.pricing?.amazonRetailPrice?.toFixed(2)} — {p.title.slice(0, 68)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                    Quantity
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={newOrderForm.quantity}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, quantity: Math.max(1, Number(e.target.value) || 1) })}
                    style={{ width: '100%', height: 42, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 14, fontWeight: 800, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                    Channel
                  </label>
                  <select
                    value={newOrderForm.channel}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, channel: e.target.value })}
                    style={{ width: '100%', height: 42, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, fontWeight: 700, background: '#FFFFFF' }}
                  >
                    {CHANNEL_OPTIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                    Initial Status
                  </label>
                  <select
                    value={newOrderForm.orderStatus}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, orderStatus: e.target.value as OrderStatus })}
                    style={{ width: '100%', height: 42, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, fontWeight: 700, background: '#FFFFFF' }}
                  >
                    {ORDER_STATUS_OPTIONS.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                    Customer Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newOrderForm.customerName}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, customerName: e.target.value })}
                    placeholder="e.g. Alexander Sterling"
                    style={{ width: '100%', height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                    Customer Order # (Optional — Auto-generated if blank)
                  </label>
                  <input
                    type="text"
                    value={newOrderForm.customerOrderNum}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, customerOrderNum: e.target.value })}
                    placeholder="AMZ-114-..."
                    style={{ width: '100%', height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                  Shipping Destination Address
                </label>
                <input
                  type="text"
                  required
                  value={newOrderForm.customerLocation}
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, customerLocation: e.target.value })}
                  style={{ width: '100%', height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    background: '#F3F4F6',
                    color: '#09090B',
                    border: '1px solid #D4D4D8',
                    borderRadius: 8,
                    padding: '11px 18px',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingOrder}
                  style={{
                    background: '#09090B',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: 8,
                    padding: '11px 22px',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  {savingOrder ? 'Creating Order...' : 'Create Order & Lock Profit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD ADDITIONAL ITEM TO EXISTING ORDER TOP-LAYER DIALOG */}
      <dialog
        ref={addItemDialogRef}
        className="add-order-item-dialog"
        aria-labelledby="add-order-item-modal-title"
        onClose={() => setAddItemModalOrder(null)}
        onClick={(e) => {
          if (e.target === addItemDialogRef.current) {
            setAddItemModalOrder(null);
          }
        }}
        style={{
          padding: 0,
          border: 'none',
          borderRadius: 16,
          background: 'transparent',
          maxWidth: 660,
          width: 'calc(100vw - 40px)',
          margin: 'auto',
          overflow: 'visible'
        }}
      >
        {addItemModalOrder && (() => {
          const selectedProd = products.find((p) => p.id === addItemForm.productId) || products[0];
          const addQty = Math.max(1, Number(addItemForm.quantity || 1));
          const unitRetailPreview =
            addItemForm.pricingMode === 'free_bundle'
              ? 0
              : addItemForm.mode === 'catalog'
              ? Number(selectedProd?.pricing?.amazonRetailPrice || 34.99)
              : Number(addItemForm.customUnitRetail || 0);
          const unitCostPreview =
            addItemForm.mode === 'catalog'
              ? Number(selectedProd?.researchDossier?.usDropshipCost || selectedProd?.pricing?.alibabaFobUnit || 12.5)
              : Number(addItemForm.customUnitCost || 0);
          const nextRetailPreview = +(Number(addItemModalOrder.retailCollected || 0) + unitRetailPreview * addQty).toFixed(2);
          const nextCostPreview = +(Number(addItemModalOrder.usSupplierCostCharged || 0) + unitCostPreview * addQty).toFixed(2);
          const refRatePreview = String(addItemModalOrder.channel || '').toLowerCase().includes('shopify')
            ? 0.029
            : String(addItemModalOrder.channel || '').toLowerCase().includes('tiktok')
            ? 0.06
            : 0.15;
          const nextNetProfitPreview = +(nextRetailPreview - nextCostPreview - +(nextRetailPreview * refRatePreview).toFixed(2)).toFixed(2);

          return (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#FFFFFF',
                color: '#09090B',
                borderRadius: 16,
                border: '1px solid #E4E4E7',
                width: '100%',
                maxHeight: '88vh',
                overflowY: 'auto',
                padding: 28,
                boxSizing: 'border-box',
                boxShadow: '0 24px 56px rgba(9, 9, 11, 0.36)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    MULTI-ITEM ORDER &amp; CJ DROPSHIP DISPATCH · {addItemModalOrder.customerOrderNum}
                  </span>
                  <h2 id="add-order-item-modal-title" style={{ fontSize: 21, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0' }}>
                    Add Additional Item to Order
                  </h2>
                  <div style={{ fontSize: 12, color: '#52525B', marginTop: 3 }}>
                    Recipient: <strong>{addItemModalOrder.customerName}</strong> ({addItemModalOrder.customerLocation})
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAddItemModalOrder(null)}
                  style={{
                    background: '#F3F4F6',
                    border: 'none',
                    borderRadius: 8,
                    width: 36,
                    height: 36,
                    fontSize: 16,
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddAdditionalItemSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* SOURCE MODE SWITCHER: CATALOG PRODUCT VS CUSTOM CJ SKU */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {[
                    { key: 'catalog', label: `1. Pick from Catalog (${products.length} SKUs)` },
                    { key: 'custom', label: '2. Custom Item / External CJ SKU' }
                  ].map((tab) => {
                    const activeMode = addItemForm.mode === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setAddItemForm({ ...addItemForm, mode: tab.key as 'catalog' | 'custom' })}
                        style={{
                          background: activeMode ? '#09090B' : '#F8F8F9',
                          color: activeMode ? '#FFFFFF' : '#3F3F46',
                          border: activeMode ? '1px solid #09090B' : '1px solid #D4D4D8',
                          borderRadius: 8,
                          padding: '10px 12px',
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {addItemForm.mode === 'catalog' ? (
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}>
                      Search &amp; Select Catalog Product to Add
                    </label>
                    <input
                      type="search"
                      value={addItemForm.productSearch}
                      onChange={(e) => setAddItemForm({ ...addItemForm, productSearch: e.target.value })}
                      placeholder="Search by product title, SKU (AUR-GEN-...), ASIN, or CJ SKU..."
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 13,
                        marginBottom: 8,
                        boxSizing: 'border-box'
                      }}
                    />
                    <select
                      value={addItemForm.productId}
                      onChange={(e) => setAddItemForm({ ...addItemForm, productId: e.target.value })}
                      style={{
                        width: '100%',
                        height: 44,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: '1px solid #09090B',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#09090B',
                        background: '#FAF6EC'
                      }}
                    >
                      {filteredAddItemProducts.map((p) => (
                        <option key={p.id} value={p.id}>
                          #{p.rank} · {p.amazonListing?.amazonSku || p.amazonListing?.genericSku || p.id} · Retail ${p.pricing?.amazonRetailPrice?.toFixed(2)} (CJ Cost ${(p.researchDossier?.usDropshipCost || p.pricing?.alibabaFobUnit || 12.5).toFixed(2)}) — {p.title.slice(0, 60)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                        Item Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={addItemForm.customTitle}
                        onChange={(e) => setAddItemForm({ ...addItemForm, customTitle: e.target.value })}
                        placeholder="e.g. Microfiber Replacement Mop Pads (6-Pack)"
                        style={{ width: '100%', height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                          Store SKU (Optional)
                        </label>
                        <input
                          type="text"
                          value={addItemForm.customSku}
                          onChange={(e) => setAddItemForm({ ...addItemForm, customSku: e.target.value })}
                          placeholder="AUR-ADDON-01"
                          style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                          CJ Dropshipping SKU / PID / VID
                        </label>
                        <input
                          type="text"
                          value={addItemForm.customCjSku}
                          onChange={(e) => setAddItemForm({ ...addItemForm, customCjSku: e.target.value })}
                          placeholder="e.g. CJJT1714191"
                          style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                          Unit Retail Price ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={addItemForm.customUnitRetail}
                          onChange={(e) => setAddItemForm({ ...addItemForm, customUnitRetail: Number(e.target.value) || 0 })}
                          style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, fontWeight: 700, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                          Unit CJ Supplier Cost ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={addItemForm.customUnitCost}
                          onChange={(e) => setAddItemForm({ ...addItemForm, customUnitCost: Number(e.target.value) || 0 })}
                          style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 8, border: '1px solid #D4D4D8', fontSize: 13, fontWeight: 700, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* QUANTITY & PRICING MODE */}
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                      Quantity
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={addItemForm.quantity}
                      onChange={(e) => setAddItemForm({ ...addItemForm, quantity: Math.max(1, Number(e.target.value) || 1) })}
                      style={{
                        width: '100%',
                        height: 42,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 14,
                        fontWeight: 800,
                        boxSizing: 'border-box',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                      Order Financial Treatment
                    </label>
                    <select
                      value={addItemForm.pricingMode}
                      onChange={(e) => setAddItemForm({ ...addItemForm, pricingMode: e.target.value as 'standard_addon' | 'free_bundle' })}
                      style={{
                        width: '100%',
                        height: 42,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: '1px solid #D4D4D8',
                        fontSize: 13,
                        fontWeight: 700,
                        background: '#FFFFFF',
                        color: '#09090B'
                      }}
                    >
                      <option value="standard_addon">
                        Standard Paid Add-On (+{formatMoney(unitRetailPreview * addQty)} Retail &amp; +{formatMoney(unitCostPreview * addQty)} CJ Cost)
                      </option>
                      <option value="free_bundle">
                        Bundle / Replacement / Bonus Gift ($0.00 Retail · +{formatMoney(unitCostPreview * addQty)} CJ Cost Only)
                      </option>
                    </select>
                  </div>
                </div>

                {/* LIVE FINANCIAL IMPACT PREVIEW */}
                <div
                  style={{
                    background: '#09090B',
                    color: '#FFFFFF',
                    borderRadius: 10,
                    padding: 14,
                    fontVariantNumeric: 'tabular-nums',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 10
                  }}
                >
                  <div>
                    <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                      Updated Retail Total
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#FFFFFF', marginTop: 2 }}>
                      {formatMoney(nextRetailPreview)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                      Updated CJ Cost
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#D9B668', marginTop: 2 }}>
                      {formatMoney(nextCostPreview)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                      Updated Net Profit
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: nextNetProfitPreview >= 0 ? '#4ADE80' : '#F87171', marginTop: 2 }}>
                      {nextNetProfitPreview >= 0 ? '+' : ''}{formatMoney(nextNetProfitPreview)}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => setAddItemModalOrder(null)}
                    style={{
                      background: '#F3F4F6',
                      color: '#09090B',
                      border: '1px solid #D4D4D8',
                      borderRadius: 8,
                      padding: '11px 18px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingOrder}
                    style={{
                      background: '#09090B',
                      color: '#D9B668',
                      border: '1px solid #D9B668',
                      borderRadius: 8,
                      padding: '11px 22px',
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: 'pointer'
                    }}
                  >
                    {savingOrder ? 'Adding Item & Updating CJ...' : '+ Add Item & Save to Order'}
                  </button>
                </div>
              </form>
            </div>
          );
        })()}
      </dialog>
    </section>
  );
};
